import { SidebarType } from '@scalar-org/entities/project'

import type { StaticDocError } from '@scalar-org/entities/publish'

import type { Page, Project } from '@scalar-org/entities/project'
import type { StaticDoc } from '@scalar-org/entities/publish'
import { mergeNonNullish } from '@scalar-org/helpers/objects'
import { renderMarkdownContent } from './render'
import type { PageMeta } from '@scalar-org/scalar-config'

export type ServerState = {
  /** Save a record of the page content and location for atomic updates when files are changed */
  pages: Record<
    string,
    {
      page: Page
      guideUid: string
      versionUid: string
    }
  >
  references: Record<
    string,
    {
      versionUid: string
      content: string
    }
  >
  pageContent: Record<string, StaticDoc | StaticDocError>
}

export function createState(): ServerState {
  return {
    pages: {},
    references: {},
    pageContent: {},
  }
}

/** Load the config into the state and render any markdown files */
export async function loadConfigToState({
  project,
  documents,
  references,
  state,
}: {
  project: Project
  documents: Record<string, string>
  references: Record<string, string>
  state: ServerState
}) {
  // We need to load every page into the map to allow quick lookups when returning the content
  Object.values(project.versions).forEach((version) => {
    Object.values(version.guides).forEach((guide) => {
      Object.values(guide.sidebar.items).forEach((item) => {
        if (item.type === SidebarType.Page) {
          state.pages[item.uid] = {
            page: item,
            guideUid: guide.uid,
            versionUid: version.uid,
          }
        }
      })
    })
  })

  /** We save all the doc */
  for (const uid of Object.keys(documents)) {
    state.pageContent[uid] = await renderMarkdownContent(
      state.pages[uid].page,
      documents[uid],
    )
  }

  Object.keys(references).forEach((uid) => {
    state.references[uid] = {
      versionUid: references[uid],
      content: references[uid],
    }
  })
}

/** Update the rendered content of a page in the server state */
export async function updatePageState({
  state,
  uid,
  content,
  meta,
}: {
  state: ServerState
  uid: string
  content: string
  meta: PageMeta
}) {
  // Update any page meta data that has changed
  state.pages[uid].page = mergeNonNullish(state.pages[uid].page, meta)

  state.pageContent[uid] = await renderMarkdownContent(
    state.pages[uid].page,
    content,
  )
}
